// =============================================================================
// Step 3 — Slot Selection con suggerimenti multi-giorno e preview calendario
// =============================================================================

import { format, parseISO, isToday, isTomorrow } from 'date-fns';
import {
  User,
  Users,
  Briefcase,
  Settings2,
  Clock,
  ChevronLeft,
  ChevronRight,
  Loader2,
  AlertCircle,
  Sparkles,
  ArrowRight,
  CalendarCheck2,
  Search,
  MapPin,
  Zap,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDateLocale } from '../../../hooks/useDateLocale';
import { cn } from '../../../lib/utils';
import { L } from '../../../config/labels';
import { useBooking, type DaySuggestion } from './BookingContext';
import type { AvailableSlot } from './types';
import { useTenantTimeZone } from '../../../store/tenantStore';
import { timeStringInTz } from '../../../lib/tenantTime';

// ===========================================================================
// Main Step Component
// ===========================================================================

export function SlotConfirmStep() {
  const timeZone = useTenantTimeZone();
  const { t } = useTranslation('scheduler');
  const dateLocale = useDateLocale();
  const {
    state,
    dispatch,
    selectedService,
    selectedCustomer,
    resourceTypes,
    availableSlots,
    slotsLoading,
    slotsError,
    slotStart,
    slotEnd,
    handleSlotSelect,
    handleDateChange,
    firstAvailableSlot,
    firstAvailableLoading,
    handleGoToFirstAvailable,
    multiDaySuggestions,
    multiDaySuggestionsLoading,
    handleNavigateToSuggestion,
  } = useBooking();

  return (
    <div className="space-y-5">
      {/* Booking Summary (compact) */}
      <BookingSummary
        state={state}
        selectedService={selectedService}
        selectedCustomer={selectedCustomer}
        resourceTypes={resourceTypes}
      />

      {/* Date Navigation */}
      <div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleDateChange('prev')}
            className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <ChevronLeft className="w-5 h-5 text-gray-500" />
          </button>
          <div className="flex-1 text-center">
            <div className="font-semibold text-gray-900">
              {format(state.slotDate, 'EEEE d MMMM', { locale: dateLocale })}
            </div>
            <div className="text-xs text-gray-500">
              {isToday(state.slotDate)
                ? t('suggestions.today')
                : isTomorrow(state.slotDate)
                  ? t('suggestions.tomorrow')
                  : format(state.slotDate, 'yyyy', { locale: dateLocale })}
            </div>
          </div>
          <button
            onClick={() => handleDateChange('next')}
            className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <ChevronRight className="w-5 h-5 text-gray-500" />
          </button>
        </div>
      </div>

      {/* Available Slots for Current Day */}
      <div>
        <label className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
          <Clock className="w-3.5 h-3.5" />
          {t('slots.available')}
        </label>

        {slotsLoading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="w-5 h-5 animate-spin text-emerald-600" />
            <span className="ml-2 text-sm text-gray-500">{t('slots.loading')}</span>
          </div>
        ) : slotsError ? (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-center gap-2 text-sm">
            <AlertCircle className="w-4 h-4" />
            {t('slots.loadError')}
          </div>
        ) : availableSlots.length === 0 ? (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-700 text-center text-sm">
            <p className="font-medium">{t('slots.noSlots')}</p>
            <p className="text-xs mt-1 text-amber-600">{t('slots.trySwitchDay')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-4 gap-1.5">
            {availableSlots.map((slot: AvailableSlot, index: number) => {
              const slotTime = parseISO(slot.start);
              const isSelected = state.selectedAvailableSlot?.start === slot.start;
              const joinable = slot.joinable;
              return (
                <button
                  key={index}
                  onClick={() => handleSlotSelect(slot)}
                  title={joinable ? t('slots.joinExisting') : undefined}
                  className={cn(
                    'px-2 py-1.5 rounded-lg text-sm font-medium transition-all text-center',
                    'border hover:shadow-sm',
                    isSelected
                      ? 'bg-emerald-500 border-emerald-500 text-white shadow-md ring-2 ring-emerald-200'
                      : joinable
                        ? 'bg-indigo-50 border-indigo-300 text-indigo-700 hover:border-indigo-400 hover:bg-indigo-100'
                        : 'bg-white border-gray-200 text-gray-700 hover:border-emerald-400 hover:bg-emerald-50',
                  )}
                >
                  <span className="block">{timeStringInTz(slotTime, timeZone)}</span>
                  {joinable && (
                    <span
                      className={cn(
                        'mt-0.5 flex items-center justify-center gap-0.5 text-[10px] font-semibold',
                        isSelected ? 'text-emerald-100' : 'text-indigo-500',
                      )}
                    >
                      <Users className="w-3 h-3" />
                      {joinable.currentClients}/{joinable.maxClients}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
        {/* Legenda slot joinabili */}
        {availableSlots.some((s: AvailableSlot) => s.joinable) && (
          <p className="mt-2 text-xs text-indigo-500 flex items-center gap-1">
            <Users className="w-3 h-3" />
            {t('slots.joinLegend')}
          </p>
        )}

        {/* Manual Time Input Toggle */}
        <div className="mt-3 pt-3 border-t border-gray-100">
          <button
            onClick={() => {
              dispatch({
                type: 'SET_SHOW_MANUAL_TIME',
                payload: !state.showManualTimeInput,
              });
            }}
            className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1"
          >
            {state.showManualTimeInput
              ? t('slots.backToSuggested')
              : t('slots.manualTime')}
          </button>

          {state.showManualTimeInput && (
            <div className="mt-2">
              <input
                type="time"
                value={state.manualStartTime}
                onChange={e =>
                  dispatch({ type: 'SET_MANUAL_TIME', payload: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 text-sm"
              />
              <p className="text-xs text-amber-600 mt-1.5 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {t('slots.manualWarning')}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Selected Slot Summary */}
      {slotStart && slotEnd && (
        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
              <CalendarCheck2 className="w-4 h-4 text-emerald-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs text-emerald-600 font-medium">{t('slots.selectedSlot')}</div>
              <div className="text-lg font-bold text-emerald-800">
                {timeStringInTz(slotStart, timeZone)} - {timeStringInTz(slotEnd, timeZone)}
              </div>
            </div>
          </div>
          {state.selectedAvailableSlot?.allocations &&
            state.selectedAvailableSlot.allocations.length > 0 && (
              <div className="flex items-center gap-1 mt-2 text-xs text-emerald-600">
                <MapPin className="w-3 h-3" />
                {state.selectedAvailableSlot.allocations.map(a => a.resourceName).join(', ')}
              </div>
            )}

          {/* Slot joinable: unisciti all'evento esistente o creane uno nuovo */}
          {state.selectedAvailableSlot?.joinable && (
            <div className="mt-2 pt-2 border-t border-emerald-200">
              {state.selectedAvailableSlot.joinable.resourcesFree ? (
                <div className="space-y-1.5">
                  <div className="grid grid-cols-2 gap-1.5">
                    <button
                      onClick={() => dispatch({ type: 'SET_JOIN_MODE', payload: 'join' })}
                      className={cn(
                        'px-2 py-1.5 rounded-lg text-xs font-medium border transition-all flex items-center justify-center gap-1',
                        state.joinMode === 'join'
                          ? 'bg-indigo-600 border-indigo-600 text-white shadow-sm'
                          : 'bg-white border-gray-200 text-gray-600 hover:border-indigo-300',
                      )}
                    >
                      <Users className="w-3.5 h-3.5" />
                      {t('slots.joinExistingOption')}
                    </button>
                    <button
                      onClick={() => dispatch({ type: 'SET_JOIN_MODE', payload: 'create' })}
                      className={cn(
                        'px-2 py-1.5 rounded-lg text-xs font-medium border transition-all',
                        state.joinMode === 'create'
                          ? 'bg-emerald-600 border-emerald-600 text-white shadow-sm'
                          : 'bg-white border-gray-200 text-gray-600 hover:border-emerald-300',
                      )}
                    >
                      {t('slots.createNewOption')}
                    </button>
                  </div>
                  {state.joinMode === 'join' && (
                    <p className="text-xs text-indigo-600">
                      {t('slots.joinSelectedInfo', {
                        current: state.selectedAvailableSlot.joinable.currentClients,
                        max: state.selectedAvailableSlot.joinable.maxClients,
                      })}
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-xs text-indigo-600 flex items-center gap-1.5 font-medium">
                  <Users className="w-3.5 h-3.5" />
                  {t('slots.joinSelectedInfo', {
                    current: state.selectedAvailableSlot.joinable.currentClients,
                    max: state.selectedAvailableSlot.joinable.maxClients,
                  })}
                </p>
              )}
            </div>
          )}

          <p className="text-xs text-emerald-500 mt-1.5 italic">
            {t('suggestions.previewHint')}
          </p>
        </div>
      )}

      {/* Capienza del nuovo evento (solo servizi di gruppo, non in join) */}
      {state.bookingMode === 'catalog' &&
        selectedService &&
        selectedService.maxClients > 1 &&
        !(state.selectedAvailableSlot?.joinable && state.joinMode === 'join') && (
          <div className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg">
            <div>
              <div className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                <Users className="w-4 h-4 text-gray-400" />
                {t('booking.maxClients')}
              </div>
              <p className="text-xs text-gray-500 mt-0.5">
                {t('booking.maxClientsDefaultHint', { value: selectedService.maxClients })}
              </p>
            </div>
            <input
              type="number"
              min={1}
              max={500}
              value={state.maxClientsOverride ?? selectedService.maxClients}
              onChange={(e) => {
                const v = Math.max(1, parseInt(e.target.value) || 1);
                dispatch({
                  type: 'SET_MAX_CLIENTS_OVERRIDE',
                  payload: v === selectedService.maxClients ? null : v,
                });
              }}
              className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-sm text-center focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>
        )}

      {/* Multi-Day Suggestions */}
      <MultiDaySuggestions
        suggestions={multiDaySuggestions}
        loading={multiDaySuggestionsLoading}
        firstAvailableSlot={firstAvailableSlot}
        firstAvailableLoading={firstAvailableLoading}
        onGoToFirstAvailable={handleGoToFirstAvailable}
        onNavigateToSuggestion={handleNavigateToSuggestion}
        noSlotsToday={availableSlots.length === 0 && !slotsLoading && !slotsError}
      />

      {/* Optimization Panel */}
      {state.showOptimizationPanel && <OptimizationPanel />}
    </div>
  );
}

// ===========================================================================
// Multi-Day Suggestions
// ===========================================================================

interface MultiDaySuggestionsProps {
  suggestions: DaySuggestion[];
  loading: boolean;
  firstAvailableSlot: ReturnType<typeof useBooking>['firstAvailableSlot'];
  firstAvailableLoading: boolean;
  onGoToFirstAvailable: () => void;
  onNavigateToSuggestion: (suggestion: DaySuggestion) => void;
  noSlotsToday: boolean;
}

function MultiDaySuggestions({
  suggestions,
  loading,
  firstAvailableSlot,
  firstAvailableLoading,
  onGoToFirstAvailable,
  onNavigateToSuggestion,
  noSlotsToday,
}: MultiDaySuggestionsProps) {
  const { t } = useTranslation('scheduler');
  const dateLocale = useDateLocale();
  const timeZone = useTenantTimeZone();

  const showFirstAvailableFallback = noSlotsToday && suggestions.length === 0;

  return (
    <div>
      <label className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
        <Zap className="w-3.5 h-3.5" />
        {t('suggestions.title')}
      </label>

      {loading ? (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-4 h-4 animate-spin text-indigo-500" />
          <span className="ml-2 text-xs text-gray-500">{t('suggestions.searching')}</span>
        </div>
      ) : suggestions.length > 0 ? (
        <div className="space-y-1.5">
          {suggestions.map((suggestion) => (
            <button
              key={suggestion.date.toISOString()}
              onClick={() => onNavigateToSuggestion(suggestion)}
              className="w-full flex items-center gap-3 p-2.5 rounded-lg border border-gray-200 hover:border-indigo-300 hover:bg-indigo-50/50 transition-all text-left group"
            >
              <div className="w-10 h-10 rounded-lg bg-indigo-50 flex flex-col items-center justify-center flex-shrink-0 group-hover:bg-indigo-100 transition-colors">
                <span className="text-[10px] font-bold text-indigo-600 uppercase leading-tight">
                  {format(suggestion.date, 'EEE', { locale: dateLocale })}
                </span>
                <span className="text-sm font-bold text-indigo-800 leading-tight">
                  {format(suggestion.date, 'd')}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-800">
                  {suggestion.dateLabel}
                </div>
                <div className="text-xs text-gray-500">
                  {t('suggestions.slotsAvailable', { count: suggestion.slotCount })} - {t('suggestions.from')} {suggestion.firstSlotTime}
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-indigo-500 transition-colors" />
            </button>
          ))}
        </div>
      ) : showFirstAvailableFallback ? (
        <>
          {firstAvailableLoading ? (
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
              <div className="flex items-center justify-center gap-2 text-emerald-600">
                <Search className="w-3.5 h-3.5 animate-pulse" />
                <span className="text-xs font-medium">{t('slots.firstAvailable.searching')}</span>
              </div>
            </div>
          ) : firstAvailableSlot ? (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-start gap-2.5">
                <div className="w-9 h-9 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                  <CalendarCheck2 className="w-4 h-4 text-green-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-green-800">
                    {t('slots.firstAvailable.title')}
                  </div>
                  <div className="text-sm font-bold text-green-900 mt-0.5">
                    {format(parseISO(firstAvailableSlot.start), 'EEEE d MMMM', { locale: dateLocale })}
                    {' · '}
                    {timeStringInTz(parseISO(firstAvailableSlot.start), timeZone)}
                  </div>
                  {firstAvailableSlot.allocations?.length > 0 && (
                    <div className="text-xs text-green-600 mt-0.5">
                      {firstAvailableSlot.allocations.map(a => a.resourceName).join(', ')}
                    </div>
                  )}
                </div>
              </div>
              <button
                onClick={onGoToFirstAvailable}
                className="mt-2.5 w-full px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium text-xs flex items-center justify-center gap-1.5"
              >
                <CalendarCheck2 className="w-3.5 h-3.5" />
                {t('slots.firstAvailable.goToDate')}
              </button>
            </div>
          ) : !firstAvailableLoading ? (
            <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-500 text-center text-xs">
              {t('slots.firstAvailable.noneFound')}
            </div>
          ) : null}
        </>
      ) : (
        <div className="p-2.5 bg-gray-50 rounded-lg text-gray-400 text-center text-xs">
          {t('suggestions.allDaysShown')}
        </div>
      )}
    </div>
  );
}

// ===========================================================================
// Booking Summary (compact version for panel)
// ===========================================================================

interface BookingSummaryProps {
  state: ReturnType<typeof useBooking>['state'];
  selectedService: ReturnType<typeof useBooking>['selectedService'];
  selectedCustomer: ReturnType<typeof useBooking>['selectedCustomer'];
  resourceTypes: ReturnType<typeof useBooking>['resourceTypes'];
}

function BookingSummary({
  state,
  selectedService,
  selectedCustomer,
  resourceTypes,
}: BookingSummaryProps) {
  const { t } = useTranslation('scheduler');

  if (state.bookingMode === 'catalog') {
    return (
      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
        <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
          <Briefcase className="w-4 h-4 text-emerald-600" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm text-gray-900 truncate">
            {selectedService?.name}
          </div>
          <div className="text-xs text-gray-500 flex items-center gap-2">
            <span>{selectedService?.durationMinutes} min</span>
            {selectedService?.basePrice != null && <span>€{selectedService.basePrice}</span>}
            {selectedService?.requiresClient !== false && selectedCustomer && (
              <>
                <span className="text-gray-300">·</span>
                <span className="flex items-center gap-1">
                  <User className="w-3 h-3" />
                  {selectedCustomer.firstName} {selectedCustomer.lastName}
                </span>
              </>
            )}
            {selectedService?.requiresClient === false && (
              <>
                <span className="text-gray-300">·</span>
                <span className="text-amber-600 font-medium">{t('internalActivity')}</span>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
      <div
        className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: state.customEvent.color + '20' }}
      >
        <Settings2 className="w-4 h-4" style={{ color: state.customEvent.color }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm text-gray-900 truncate">
          {state.customEvent.title}
        </div>
        <div className="text-xs text-gray-500 flex items-center gap-2">
          <span>{state.customEvent.duration} min</span>
          {state.customEvent.requiresClient && selectedCustomer && (
            <>
              <span className="text-gray-300">·</span>
              <span className="flex items-center gap-1">
                <User className="w-3 h-3" />
                {selectedCustomer.firstName} {selectedCustomer.lastName}
              </span>
            </>
          )}
          {state.customEvent.resourceRequirements.length > 0 && (
            <>
              <span className="text-gray-300">·</span>
              <span>
                {state.customEvent.resourceRequirements
                  .map(r => {
                    const rt = resourceTypes.find(rt => rt.id === r.resourceTypeId);
                    return `${rt?.name || '?'} x${r.quantity}`;
                  })
                  .join(', ')}
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// Optimization Panel
// ===========================================================================

function OptimizationPanel() {
  const { t } = useTranslation('scheduler');
  const timeZone = useTenantTimeZone();
  const {
    state,
    dispatch,
    optimizeSuggestion,
    overrideBooking,
    handleApplyOptimizationAndBook,
  } = useBooking();

  const suggestion = state.optimizationSuggestion;

  return (
    <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
      <div className="flex items-center gap-2 text-purple-700 font-medium mb-2 text-sm">
        <Sparkles className="w-4 h-4" />
        {t('optimization.title')}
      </div>

      {optimizeSuggestion.isPending ? (
        <div className="flex items-center gap-2 text-purple-600 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" />
          {t('optimization.analyzing')}
        </div>
      ) : suggestion?.canOptimize ? (
        <div className="space-y-2">
          <div className="bg-white rounded-lg p-2.5 border border-purple-100 text-sm">
            <div className="text-xs text-gray-500">{t('optimization.appointmentToMove', { entity: L.appointment })}</div>
            <div className="font-medium text-gray-900">{suggestion.blockingAppointment?.serviceName}</div>
            <div className="text-xs text-gray-500">
              {t('optimization.currently')}{' '}
              {suggestion.blockingAppointment?.currentStart
                ? timeStringInTz(parseISO(suggestion.blockingAppointment.currentStart), timeZone)
                : ''}{' - '}
              {suggestion.blockingAppointment?.currentEnd
                ? timeStringInTz(parseISO(suggestion.blockingAppointment.currentEnd), timeZone)
                : ''}
            </div>
          </div>

          <div className="flex items-center justify-center">
            <ArrowRight className="w-4 h-4 text-purple-400" />
          </div>

          <div className="bg-green-50 rounded-lg p-2.5 border border-green-200 text-sm">
            <div className="text-xs text-green-700 font-medium">{t('optimization.suggestedTime')}</div>
            <div className="text-base font-bold text-green-800">
              {suggestion.suggestedMove?.newStart
                ? timeStringInTz(parseISO(suggestion.suggestedMove.newStart), timeZone)
                : ''}{' - '}
              {suggestion.suggestedMove?.newEnd
                ? timeStringInTz(parseISO(suggestion.suggestedMove.newEnd), timeZone)
                : ''}
            </div>
          </div>

          <button
            onClick={handleApplyOptimizationAndBook}
            disabled={overrideBooking.isPending}
            className="w-full px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 font-medium text-sm flex items-center justify-center gap-2"
          >
            {overrideBooking.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {t('optimization.applying')}
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4" />
                {t('optimization.applyAndBook')}
              </>
            )}
          </button>
        </div>
      ) : (
        <div className="text-sm text-purple-700">
          <p>{suggestion?.reason || t('optimization.cannotOptimize')}</p>
        </div>
      )}

      <button
        onClick={() => {
          dispatch({ type: 'SET_SHOW_OPTIMIZATION_PANEL', payload: false });
          dispatch({ type: 'SET_OPTIMIZATION', payload: null });
        }}
        className="mt-2 text-xs text-purple-600 hover:text-purple-800"
      >
        {t('optimization.closeSuggestion')}
      </button>
    </div>
  );
}
